/**
 * TradeTicket Component
 * 
 * Order entry form
 * 
 * Version: 6.2.0
 */
import React, { useState } from 'react'
import './TradeTicket.css'

interface TradeTicketProps {
  symbol?: string
  lastPrice?: number
  onSubmit?: (order: OrderData) => void
  className?: string
}

interface OrderData {
  symbol: string
  type: 'BUY' | 'SELL'
  quantity: number
  priceType: 'MARKET' | 'LIMIT' | 'SL' | 'SL-M'
  price?: number
  triggerPrice?: number
}

const TradeTicket: React.FC<TradeTicketProps> = ({
  symbol = 'NIFTY',
  lastPrice = 24891.45,
  onSubmit,
  className = ''
}) => {
  const [orderType, setOrderType] = useState<'BUY' | 'SELL'>('BUY')
  const [quantity, setQuantity] = useState(1)
  const [priceType, setPriceType] = useState<OrderData['priceType']>('MARKET')
  const [limitPrice, setLimitPrice] = useState(lastPrice.toFixed(2))
  const [triggerPrice, setTriggerPrice] = useState((lastPrice - 50).toFixed(2))
  const [isSubmitting, setIsSubmitting] = useState(false)

  const orderTypes: OrderData['priceType'][] = ['MARKET', 'LIMIT', 'SL', 'SL-M']

  const handleSubmit = async () => {
    setIsSubmitting(true)
    
    const order: OrderData = {
      symbol,
      type: orderType,
      quantity,
      priceType,
      price: ['LIMIT', 'SL-M'].includes(priceType) ? parseFloat(limitPrice) : undefined,
      triggerPrice: ['SL', 'SL-M'].includes(priceType) ? parseFloat(triggerPrice) : undefined
    }

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    onSubmit?.(order)
    setIsSubmitting(false)
    
    // Reset form
    setQuantity(1)
    setPriceType('MARKET')
  }

  const estimatedValue = quantity * (priceType === 'MARKET' ? lastPrice : parseFloat(limitPrice))

  return (
    <div className={`trade-ticket ${className}`}>
      {/* Header */}
      <div className="ticket-header">
        <span className="ticket-symbol">📈 {symbol}</span>
        <span className="ticket-price">₹{lastPrice.toFixed(2)}</span>
      </div>

      {/* Buy/Sell Toggle */}
      <div className="order-type-toggle">
        <button
          className={`type-btn buy ${orderType === 'BUY' ? 'active' : ''}`}
          onClick={() => setOrderType('BUY')}
        >
          BUY
        </button>
        <button
          className={`type-btn sell ${orderType === 'SELL' ? 'active' : ''}`}
          onClick={() => setOrderType('SELL')}
        >
          SELL
        </button>
      </div>

      {/* Price Type */}
      <div className="ticket-section">
        <label className="section-label">Order Type</label>
        <div className="price-type-grid">
          {orderTypes.map((type) => (
            <button
              key={type}
              className={`price-type-btn ${priceType === type ? 'active' : ''}`}
              onClick={() => setPriceType(type)}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Limit Price */}
      {['LIMIT', 'SL-M'].includes(priceType) && (
        <div className="ticket-section">
          <label className="section-label">Limit Price</label>
          <div className="price-input-wrapper">
            <span className="currency">₹</span>
            <input
              type="number"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              className="price-input"
              step="0.05"
            />
          </div>
        </div>
      )}

      {/* Trigger Price */}
      {['SL', 'SL-M'].includes(priceType) && (
        <div className="ticket-section">
          <label className="section-label">Trigger Price</label>
          <div className="price-input-wrapper">
            <span className="currency">₹</span>
            <input
              type="number"
              value={triggerPrice}
              onChange={(e) => setTriggerPrice(e.target.value)}
              className="price-input"
              step="0.05"
            />
          </div>
        </div>
      )}

      {/* Quantity */}
      <div className="ticket-section">
        <label className="section-label">Quantity</label>
        <div className="quantity-controls">
          <button 
            className="qty-btn"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
          >
            −
          </button>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            className="qty-input"
            min="1"
          />
          <button 
            className="qty-btn"
            onClick={() => setQuantity(quantity + 1)}
          >
            +
          </button>
        </div>
      </div>

      {/* Estimated Value */}
      <div className="ticket-summary">
        <div className="summary-row">
          <span>Estimated Value</span>
          <span className="summary-value">₹{estimatedValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
        </div>
        {priceType === 'MARKET' && (
          <div className="summary-note">
            Market price at time of execution may vary
          </div>
        )}
      </div>

      {/* Submit Button */}
      <button
        className={`submit-btn ${orderType.toLowerCase()}`}
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <span className="loading-spinner" />
        ) : (
          <>
            {orderType === 'BUY' ? '🟢' : '🔴'} {orderType} {quantity} {symbol}
          </>
        )}
      </button>
    </div>
  )
}

export default TradeTicket